import { useState, useEffect, useRef, useContext } from "react";
import InputWrapper from "components/formComponents/InputWrapper/InputWrapper";
import Select from "components/formComponents/Select/Select";
import { countryCodes } from "lib/data/countrycodes";
import { UserContext } from "contexts/user";
import styles from "./PhoneInput.module.scss";

export default function PhoneInput({
  id,
  label,
  register,
  formState,
  wrapperProps,
  attributes,
  useFormMethods,
}) {
  const [phoneNumber, setPhoneNumber] = useState("");
  const { errors } = formState;

  const fullPhoneRef = useRef("");

  // User data may be stored in a cookie, which is then read into context
  const { userData } = useContext(UserContext);
  const { locationData } = userData;
  const { calling_code, country_code } = locationData;

  const [callingCodeData, setCallingCodeData] = useState(countryCodes[230]); // [230] is the default country code

  /* Must include both dial code and country code in the value
   ** to ensure the correct country is selected.
   */
  function formatCountryCodeValue(countryData) {
    const countryCodeValue = `${countryData.dial_code}-${countryData.code}`;
    return countryCodeValue;
  }

  /* When looking up country from list, use both country code and dial code
   ** to select the correct one.
   */
  function lookUpCountryDataByValue(countryCodeValue) {
    const [callingCode, countryCode] = countryCodeValue.split("-");

    const countryData = countryCodes.find(
      (country) =>
        country.dial_code === callingCode && country.code === countryCode
    );

    return countryData;
  }

  // Set the initial country code based on the user's location data
  useEffect(() => {
    if (calling_code && country_code) {
      const countryData = countryCodes.find(
        (country) => country.code === country_code
      );

      if (countryData) {
        setCallingCodeData(countryData);
      } else {
        setCallingCodeData(countryCodes[230]);
      }
    }
  }, [calling_code, country_code]);

  // Update the form value manually whenever one of the fields changes
  const { setValue, trigger } = useFormMethods;

  useEffect(() => {
    const fullPhone = !!phoneNumber
      ? `${callingCodeData.dial_code} ${phoneNumber}`
      : null;

    if (!!setValue) {
      setValue(id, fullPhone);
    }
    if (!!trigger && !!id) {
      trigger(id);
    }
  }, [callingCodeData.dial_code, phoneNumber, setValue, id, errors, trigger]);

  // Must use react-hook-form to register the field
  if (!id || !label || !register || !formState) {
    return <p>Could not load this field</p>;
  }

  // Create the options for the country code dropdown
  const countryCodeOptions = countryCodes.map((country, index) => {
    const option = {
      id: country.name,
      value: formatCountryCodeValue(country),
      label: `${country.name} (${country.dial_code})`,
      // selected: country.code === "+1" && index === 230,
    };

    return option;
  });

  /* When the country code is changed, update the callingCodeData state
   ** by using both the country code and the calling code to ensure
   ** the correct country is selected even in the case of +1 duplicates.
   */
  const handleCallingCodeChange = (value) => {
    const countryData = lookUpCountryDataByValue(value);

    if (countryData) {
      setCallingCodeData(countryData);
    }
  };

  return (
    <InputWrapper label={label} id={id} formState={formState} {...wrapperProps}>
      <div className={`${styles.phoneInputContainer}`}>
        <span className={`${styles.countryCodeDropdown}`}>
          <Select
            options={countryCodeOptions}
            controlled={false}
            onChange={(event) => {
              handleCallingCodeChange(event.target.value);
            }}
            // defaultValue={formatCountryCodeValue(countryCodes[230].dial_code)}
            value={
              callingCodeData
                ? formatCountryCodeValue(callingCodeData)
                : formatCountryCodeValue(countryCodes[230].dial_code)
            }
            // register={register}
            id="phoneCountryCode"
          />
        </span>
        <span className={`${styles.phoneInputWrapper}`}>
          <span className={`${styles.phoneInputPrefix}`}>
            {callingCodeData.dial_code}
          </span>
          <input
            type="tel"
            // id={id}
            name={`${id}-visible`}
            className={`textInput phoneInput ${styles.phoneInput}`}
            {...attributes}
            onChange={async (event) => {
              setPhoneNumber(() => {
                const phoneValue = event.target.value;
                if (!phoneValue) {
                  return null;
                }
                return phoneValue;
              });
              const isValid = await trigger(id);
            }}
            // {...register(id)}
            aria-invalid={errors[id] ? "true" : "false"}
          />
        </span>
        <input
          type="tel"
          id={id}
          name={id}
          className={`${styles.hiddenInput}`}
          value={`${
            !!phoneNumber
              ? `${callingCodeData.dial_code} ${!!phoneNumber}`
              : null
          }`}
          {...attributes}
          {...register(id)}
          ref={fullPhoneRef}
          // aria-invalid={errors[id] ? "true" : "false"}
        />
      </div>
    </InputWrapper>
  );
}
